import { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Box, Typography, Avatar, TextField, InputAdornment, IconButton } from "@mui/material"; // Add TextField to the import statement
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { styled } from "@mui/system";
import Button from "@mui/material/Button";
import { useLocation, useNavigate } from "react-router-dom"; // Import useNavigate from react-router-dom
import { useDispatch, useSelector } from "react-redux";
import { getMasterData, getProfileDetails, logoutClear, updateUserProfileDetails } from "../../redux/actions/appActions";
import { SET_SKILLS, SET_TOOLS_KNOWN } from "../../redux/actions/actionTypes";
import { getKeyValuePairs } from "../../redux/services/utils";
import MultiSelect from "../common/MultiSelect";
import { ROUTE_CERTIFICATION_NEW, ROUTE_COMPLETE_PROFILE, ROUTE_MAIN } from "../../constants/routes";
import ProfileTabs from "../common/ProfileTabs";
import NotificationNavButton from "../common/NotificationNavButton";
import { RootState } from "../../redux/types/reduxState";

const Header = styled("header")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "left",
  padding: "20px",
  backgroundColor: "rgb(255, 137, 96)",
}));

const Logo = styled("img")({
  aspectRatio: "1.89",
  width: "75px",
});

const Avatar1 = styled(Avatar)({
  width: "51px",
  height: "51px",
});

const CustomButton = ({ title, width, disabled }: any) => {
  return (
    <Button
      type="submit"
      disabled={disabled}
      sx={{
        backgroundColor: "#756DE9",
        borderRadius: "99px",
        fontSize: "16px",
        textTransform: "capitalize",
        width: width,
        "&:hover": {
          backgroundColor: "#756DE9",
        },
      }}
      variant="contained"
      size="small"
      style={{ padding: "15px 40px" }}
      className="w-100 mt-2 btn-pad"
    >
      {title}
    </Button>
  );
};

const StyledTextBox = styled(Box)({
  fontSize: "16px",
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: "400",
  color: "#1F2235",
  lineHeight: "1.5",
  marginLeft: "10px",
});

type FormValues = Yup.InferType<any>;

const schema = Yup.object().shape({
  skills: Yup.array().min(1, "Please select skills"),
  tools: Yup.array(),
  otherTools: Yup.string().notRequired(),
  workLinks: Yup.array().of(Yup.string().max(2048, "Link is too long").notRequired()).notRequired(),
});

const defaultValues = {
  skills: [],
  tools: [],
  otherTools: "",
  workLinks: [],
};

const SkillSetNew = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ajaxCallsInProgress = useSelector((state: any) => state.ajaxCallsInProgress);
  const userProfile = useSelector((state: RootState) => state.app.userProfile);
  const { toolsKnown, skills } = useSelector((state: any) => state.app);
  const {
    tools: newTools,
    skills: newSkills,
    workLinks: profileWorkLinks,
    work_links: profileWorkLinksSnake,
    workLink: profileWorkLink,
    work_link: profileWorkLinkSnake,
  } = userProfile || {};

  const Logout = () => {
    dispatch(logoutClear(()=>{
        navigate(ROUTE_MAIN, { replace: true });
    }));
  };

  const { handleSubmit, control, setValue, watch } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver<FormValues>(schema),
    reValidateMode: "onChange",
  });

  const workLinksHydratedRef = useRef(false);
  const skillsToolsHydratedRef = useRef(false);

  useEffect(() => {
    dispatch(getProfileDetails());

    dispatch(getMasterData("TOOLS_KNOWN", SET_TOOLS_KNOWN));
    dispatch(getMasterData("SKILL", SET_SKILLS));
  }, []);

  useEffect(() => {
    if (skillsToolsHydratedRef.current) return;
    if (!userProfile || Object.keys(userProfile).length === 0) return;

    if (newSkills?.length) {
      setValue("skills", getKeyValuePairs(newSkills));
    }
    if (newTools?.length) {
      setValue("tools", getKeyValuePairs(newTools));
    }
    skillsToolsHydratedRef.current = true;
  }, [newSkills, newTools, userProfile, setValue]);

  useEffect(() => {
    if (workLinksHydratedRef.current) return;
    if (!userProfile || Object.keys(userProfile).length === 0) return;

    const fromArray = profileWorkLinks ?? profileWorkLinksSnake;
    const fromSingle = profileWorkLink ?? profileWorkLinkSnake;

    const normalized =
      Array.isArray(fromArray) && fromArray.length
        ? fromArray.map((v) => String(v ?? "").trim()).filter(Boolean)
        : fromSingle != null && String(fromSingle).trim() !== ""
          ? [String(fromSingle).trim()]
          : [];

    setValue("workLinks", normalized.length ? normalized : []);
    workLinksHydratedRef.current = true;
  }, [
    userProfile,
    profileWorkLinks,
    profileWorkLinksSnake,
    profileWorkLink,
    profileWorkLinkSnake,
    setValue,
  ]);

  const onSubmit = (data: FormValues) => {
    const payload: Record<string, unknown> = {
      skills: data?.skills?.map((el: { value: string }) => el?.value),
      tools: data?.tools?.map((el: { value: string }) => el?.value),
      other: data?.otherTools?.split(",").map((el: string) => el?.trim()),
      workLinks: (data as any)?.workLinks
        ? (data as any).workLinks.map((v: any) => String(v ?? "").trim()).filter((v: string) => v.length > 0)
        : [],
    };
    dispatch(
      updateUserProfileDetails(payload, () =>
        navigate(location.search.includes("edit") ? `${ROUTE_COMPLETE_PROFILE}` : ROUTE_CERTIFICATION_NEW)
      )
    );
  };

  return (
    <Box display="flex" flexDirection="column" justifyContent="center" bgcolor="#eeeeee" width="100%">
      <Box
        sx={{
          display: "flex",
          backgroundColor: "rgb(255, 137, 96)",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Header onClick={() => navigate(ROUTE_COMPLETE_PROFILE)}>
          <Logo src="/images/client-website/seevi-logo.png" alt="Logo" />
        </Header>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <NotificationNavButton iconColor="#fff" />
          <Box sx={{ mt: 0, mr: 1 }} onClick={() => navigate(ROUTE_COMPLETE_PROFILE)} style={{ cursor: "pointer" }}>
            <InputAdornment position="end" style={{ color: "#fff", width: 20, height: 50}}>
              <HomeIcon  />
            </InputAdornment> 
          </Box>
          <Box sx={{ mt: 0, mr: 2 }} onClick={() => Logout()} style={{ cursor: "pointer" }}>
            <InputAdornment position="end" style={{ color: "#fff", width: 20, height: 50}}>
              <LogoutIcon  />
            </InputAdornment> 
          </Box>
        </Box>
      </Box>

      <ProfileTabs />

      <Box display="flex" flexDirection="column" alignItems="left" fontSize="14px" fontWeight="500" color="#757575">
        <Box display="flex" alignItems="center" mb={2} mt={-1} padding="5px 16px">
          <Avatar1 src="/images/avatar-one.png" alt="Avatar" />
          <StyledTextBox p={1} mt={2}>
            <Box px={1}>
              <Typography
                style={{
                  fontFamily: '"DM Sans", sans-serif !important',
                  fontSize: "14px !important",
                  fontWeight: "medium !important",
                  color: "#1F2235",
                }}
              >
                1/2
              </Typography>
              <Typography
                style={{
                  fontFamily: '"DM Sans", sans-serif !important',
                  fontSize: "14px !important",
                  fontWeight: "medium !important",
                  color: "#1F2235",
                }}
              >
                Select skill-sets which you are proud of.
              </Typography>
            </Box>
          </StyledTextBox>
        </Box>
      </Box>
      <Box
        component="form"
        sx={{
          overflowY: "auto",
          maxHeight: "calc(100vh - 200px)",
          paddingBottom: "100px",
        }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box display="flex" flexDirection="column" p={2} bgcolor="#ffffff" sx={{ borderRadius: "20px 20px 0px 0px" }}>
          <Typography mt={2}>My Skill-sets</Typography>
          <MultiSelect name="skills" control={control} options={getKeyValuePairs(skills)} creatable={true} />

          <Typography mt={2}>Tools Known</Typography>

          <MultiSelect name="tools" control={control} options={getKeyValuePairs(toolsKnown)} />

          {watch("tools")
            ?.map((el: any) => el?.value)
            .includes("Other") ? (
            <>
              <Box>
                <Typography mt={2}>Add Other Tools Known</Typography>
              </Box>
              <Controller
                name="otherTools"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    error={!!error}
                    helperText={error?.message || " "}
                    variant="outlined"
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "95px",
                        backgroundColor: "rgba(75, 68, 212, 0.04)",
                        color: "#777777",
                        height: "52",
                        boxShadow: "inset 0 0 11px 2px #dfd9d9",
                        "& fieldset": {
                          border: "none",
                        },
                        "& input": {
                          padding: "14px 24px",
                          fontWeight: 500,
                        },
                        "& input::placeholder": {
                          color: "#777777",
                          opacity: 1,
                        },
                      },
                      marginTop: "10px",
                    }}
                  />
                )}
              />
            </>
          ) : null}

          <Typography mt={2}>Work links</Typography>
          <Typography sx={{ fontSize: "12px", color: "#777", mb: 0.5 }}>
            Portfolio, GitHub, LinkedIn, or other links (optional).
          </Typography>
          {(watch("workLinks") || []).map((_: any, idx: number) => (
            <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "flex-start", mt: idx === 0 ? 0 : 1 }}>
              <Controller
                name={`workLinks.${idx}` as any}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    error={!!error}
                    helperText={error?.message || " "}
                    placeholder="https://"
                    variant="outlined"
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "95px",
                        backgroundColor: "rgba(75, 68, 212, 0.04)",
                        color: "#777777",
                        height: "52",
                        boxShadow: "inset 0 0 11px 2px #dfd9d9",
                        "& fieldset": {
                          border: "none",
                        },
                        "& input": {
                          padding: "14px 24px",
                          fontWeight: 500,
                        },
                        "& input::placeholder": {
                          color: "#777777",
                          opacity: 1,
                        },
                      },
                      marginTop: "4px",
                    }}
                  />
                )}
              />
              <IconButton
                aria-label="Remove work link"
                onClick={() => {
                  const current: any[] = (watch("workLinks") || []).slice();
                  current.splice(idx, 1);
                  setValue("workLinks", current, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
                sx={{ mt: "6px" }}
              >
                <DeleteOutline sx={{ color: "#d32f2f" }} />
              </IconButton>
            </Box>
          ))}
          <Box sx={{ mt: 1 }}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                const current: any[] = (watch("workLinks") || []).slice();
                setValue("workLinks", [...current, ""], { shouldValidate: true, shouldDirty: true });
              }}
              sx={{
                borderRadius: "99px",
                textTransform: "none",
                borderColor: "#756DE9",
                color: "#756DE9",
                "&:hover": { borderColor: "#5a50c4", backgroundColor: "rgba(117, 109, 233, 0.06)" },
              }}
            >
              Add work link
            </Button>
          </Box>
        </Box>
        {location?.search?.includes("edit") ? (
          <Box sx={{ mx: 2 }}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                justifyContent: "center",
                alignItems: "center",
                padding: "15px 64px",
                marginTop: "20px",
                marginBottom: "15px",
                fontSize: "16px",
                fontWeight: 600,
                color: "#ffffff",
                backgroundColor: "#756DE9",
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                borderRadius: "95px",
                textTransform: "none",
              }}
            >
              Save
            </Button>
          </Box>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            position="fixed"
            bottom="0"
            width="100%"
            height="100px"
            bgcolor="#ffffff"
            sx={{ borderRadius: "20px 20px 0px 0px" }}
          >
            <Box display="flex" justifyContent="space-around" width="100%" maxWidth="600px">
              <Button
                sx={{
                  backgroundColor: "white",
                  borderRadius: "99px",
                  fontSize: "16px",
                  textTransform: "capitalize",
                  width: "25%",
                  color: "#000000",
                  "&:hover ": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
                variant="contained"
                size="small"
                style={{ padding: "15px 40px" }}
                className="w-100 mt-2 btn-pad"
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
              <CustomButton title="Next Up: Certification" width="70%" disabled={!!ajaxCallsInProgress} />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SkillSetNew;
